// Shared card/die foil optics. Keep these calculations in one place so a
// foil tuning change reaches both surfaces. Input/output colour is display
// sRGB; callers in a linear lighting pipeline must convert at the boundary.
import { DEFAULT_FOIL_PARAMS, paramUniformName } from './foilParams.js';

const FOIL_UNIFORM_NAMES = ['uColorThresh', 'uDarkShineHi', 'uDarkShineLo', 'uDeckRamp', 'uDepthShine', 'uEmbossStr', 'uEnvOn', 'uEnvStr', 'uFilmIor', 'uFilmThickness', 'uGgxOn', 'uGgxStr', 'uGlintBrightness', 'uGlintDensity', 'uGlintOn', 'uGlintSharp', 'uGlintSpread', 'uHueShift', 'uIridOn', 'uIridStr', 'uIridTint', 'uMatcap', 'uMatcapOn', 'uMatcapRot', 'uMetalTint', 'uRestFloor', 'uRestHolo', 'uRough', 'uShadowStr', 'uShineHi', 'uShineLo', 'uSparkleDensity', 'uSparkleOn', 'uSparkleStr', 'uSparkleThresh', 'uThinFilmOn'];

export const FOIL_LIGHTING_DEFAULTS = Object.fromEntries(
  Object.entries(DEFAULT_FOIL_PARAMS).filter(([key]) => FOIL_UNIFORM_NAMES.includes(paramUniformName(key))),
);

export const FOIL_LIGHTING_GLSL = /* glsl */ `
  const float FOIL_PI = 3.14159265;
  uniform float uColorThresh;
  uniform float uDarkShineHi;
  uniform float uDarkShineLo;
  uniform float uDeckRamp;
  uniform float uDepthShine;
  uniform float uEmbossStr;
  uniform float uEnvOn;
  uniform float uEnvStr;
  uniform float uFilmIor;
  uniform float uFilmThickness;
  uniform float uGgxOn;
  uniform float uGgxStr;
  uniform float uGlintBrightness;
  uniform float uGlintDensity;
  uniform float uGlintOn;
  uniform float uGlintSharp;
  uniform float uGlintSpread;
  uniform float uHueShift;
  uniform float uIridOn;
  uniform float uIridStr;
  uniform float uIridTint;
  uniform sampler2D uMatcap;
  uniform float uMatcapOn;
  uniform float uMatcapRot;
  uniform float uMetalTint;
  uniform float uRestFloor;
  uniform float uRestHolo;
  uniform float uRough;
  uniform float uShadowStr;
  uniform float uShineHi;
  uniform float uShineLo;
  uniform float uSparkleDensity;
  uniform float uSparkleOn;
  uniform float uSparkleStr;
  uniform float uSparkleThresh;
  uniform float uThinFilmOn;
  float foilLuma(vec3 c) { return dot(c, vec3(0.299, 0.587, 0.114)); }
  vec3 foilMetallicRamp(float t) {
    return 0.5 + 0.38 * cos(6.28318 * (t + vec3(0.00, 0.18, 0.42)));
  }
  // Push a colour's saturation (k>1 = more vivid) around its own luminance.
  vec3 foilSatBoost(vec3 c, float k) {
    float l = dot(c, vec3(0.299, 0.587, 0.114));
    return clamp(mix(vec3(l), c, k), 0.0, 1.0);
  }

  // The exact 5-colour holo ramp from the legacy deck-builder CSS gradient
  // (#f80e35 #eedf10 #21e985 #0dbde9 #c929f1), cycled. Using these instead of
  // a cosine ramp so the WebGL colours match the deck builder.
  vec3 foilRainbow5(float t) {
    t = fract(t) * 5.0;
    float f = fract(t);
    vec3 c0 = vec3(0.973, 0.055, 0.208);
    vec3 c1 = vec3(0.933, 0.875, 0.063);
    vec3 c2 = vec3(0.129, 0.914, 0.522);
    vec3 c3 = vec3(0.051, 0.741, 0.914);
    vec3 c4 = vec3(0.788, 0.161, 0.945);
    if (t < 1.0) return mix(c0, c1, f);
    if (t < 2.0) return mix(c1, c2, f);
    if (t < 3.0) return mix(c2, c3, f);
    if (t < 4.0) return mix(c3, c4, f);
    return mix(c4, c0, f);
  }

  // Physically-based thin-film interference (Belcour-Barla style practical
  // approximation, the model standardized as glTF KHR_materials_iridescence).
  // A thin dielectric film of refractive index filmIor and thickness (nm) sits
  // on the metal. Light reflecting off the top and bottom interfaces is phase-
  // shifted by the extra optical path 2*filmIor*thickness*cosThetaFilm; that
  // phase, evaluated at three wavelengths (R/G/B), sets which colours
  // constructively interfere. We sum two-beam fringes per channel and tonemap
  // to stay in gamut.
  vec3 foilThinFilm(float cosTheta, float thickness, float filmIor) {
    cosTheta = clamp(cosTheta, 0.02, 1.0);
    // Refraction into the film (ambient IOR ~1.0) via Snell, then cos inside.
    float sinTheta2 = (1.0 - cosTheta * cosTheta) / (filmIor * filmIor);
    float cosFilm = sqrt(max(1.0 - sinTheta2, 0.0));
    // Optical path difference (nm). x2 = two passes through the film.
    float opd = 2.0 * filmIor * thickness * cosFilm;
    // Phase per channel for R/G/B reference wavelengths (nm) + a fixed pi shift
    // at the denser-medium interface.
    vec3 lambda = vec3(615.0, 535.0, 465.0);
    vec3 phase = (6.28318 * opd) / lambda + FOIL_PI;
    // Two-beam interference intensity: 0.5*(1 - cos) maps to [0,1] per channel.
    vec3 irid = 0.5 - 0.5 * cos(phase);
    // Slight desaturation toward the mean so no single channel clamps hard,
    // keeping the result smooth and in gamut.
    float m = dot(irid, vec3(0.3333));
    return clamp(mix(vec3(m), irid, 0.85), 0.0, 1.0);
  }
  float foilHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  vec2 foilHash2(vec2 p) {
    return fract(sin(vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)))) * 43758.5453);
  }

  float foilDGGX(float ndh, float rough) {
    float a = rough * rough;
    float a2 = a * a;
    float d = ndh * ndh * (a2 - 1.0) + 1.0;
    return a2 / (FOIL_PI * d * d);
  }
  float foilGSmith(float ndl, float ndv, float rough) {
    float k = rough * rough * 0.5;
    float gl = ndl / (ndl * (1.0 - k) + k);
    float gv = ndv / (ndv * (1.0 - k) + k);
    return gl * gv;
  }

  vec3 foilStudioEnv(vec2 p) {
    vec2 key = p - vec2(0.35, 0.55);
    vec2 fill = p + vec2(0.45, 0.25);
    float kl = exp(-6.0 * dot(key, key));
    float fl = exp(-9.0 * dot(fill, fill));
    float band = smoothstep(0.2, 1.0, p.y) * 0.22;
    return vec3(0.03) + vec3(1.0, 0.97, 0.90) * kl * 1.1 + vec3(0.6, 0.75, 1.0) * (fl * 0.7 + band);
  }

  float foilCoverage(vec3 base, float foilMode) {
    float Lm = foilLuma(base);
    float satv = max(max(base.r, base.g), base.b) - min(min(base.r, base.g), base.b);
    float shine;
    if (foilMode > 0.66) {
      // COLOUR: foil the STRONGEST-colour regions. The most saturated/vivid
      // pixels (Atlas Wanderers' red orb, the teal). And leave the muddy,
      // desaturated bulk matte. satv is per-pixel chroma (max-min), so this is
      // robust and self-limiting: only genuinely colourful areas foil, never the
      // whole card. colorThresh = the saturation a pixel needs before it foils.
      shine = smoothstep(uColorThresh, uColorThresh + 0.25, satv);
    } else if (foilMode > 0.33) {
      // DARK: a tight band near black, so only the darkest linework foils.
      shine = 1.0 - smoothstep(uDarkShineLo, uDarkShineHi, Lm);
    } else {
      // LIGHT: the original. Brightest art foils.
      shine = smoothstep(uShineLo, uShineHi, Lm);
    }
    return shine;
  }

  struct FoilSurface {
    vec3 base;
    vec3 N;
    vec3 V;
    vec3 Lr;
    vec2 uv0;
    vec2 puv;
    vec2 lightXY;
    float glareGuard;
    float shine;
    float foilMode;
    float whiteMask;
    float relief;
    float zoneFoil;
    float bevel;
    vec2 texel;
    vec2 tilt;
    float fromCenter;
    float sceneLit;
    float depthDelta;
  };

  // Display/sRGB colour in and out. Geometry, lighting vectors and coverage
  // come from the caller; the optical equations are shared by cards and dice.
  vec3 shadeFoilSurface(FoilSurface s) {
    vec3 H = normalize(s.V + s.Lr);
    float ndh = max(dot(s.N, H), 0.0);
    float ndl = max(dot(s.N, s.Lr), 0.0);
    float ndv = max(dot(s.N, s.V), 0.0);
    float vdh = max(dot(s.V, H), 0.0);

    float D = foilDGGX(ndh, uRough);
    float G = foilGSmith(ndl, ndv, uRough);
    vec3 F0 = vec3(0.95, 0.93, 0.88);
    vec3 F = F0 + (1.0 - F0) * pow(1.0 - vdh, 5.0);
    vec3 ggx = (D * G) * F / (4.0 * max(ndv, 0.02) * max(ndl, 0.02) + 1e-3) * ndl;
    ggx = min(ggx, vec3(2.5));

    // Env reflection direction. DOM: the tilt-swept normal model. Board: the
    // true reflection of the view vector about the s.relief normal, so the studio
    // env genuinely mirrors what the camera sees as it orbits.
    vec3 reflTS = reflect(-s.V, s.N);
    vec2 envDir = (s.sceneLit > 0.5) ? reflTS.xy : (s.N.xy * 0.9 + s.lightXY * 1.6);
    vec3 env;
    if (uMatcapOn > 0.5) {
      // Real environment reflection: map the tilt-swept normal into the studio
      // matcap. Classic matcap UV is s.N.xy * 0.5 + 0.5; we add the tilt so the
      // studio sweeps as the card moves, then rotate the lookup by uMatcapRot.
      vec2 refl = (s.sceneLit > 0.5) ? reflTS.xy : (s.N.xy + s.lightXY * 0.6);
      float cr = cos(uMatcapRot);
      float sr = sin(uMatcapRot);
      vec2 rot = vec2(refl.x * cr - refl.y * sr, refl.x * sr + refl.y * cr);
      vec2 muv = clamp(rot * 0.5 + 0.5, 0.0, 1.0);
      env = texture2D(uMatcap, muv).rgb * F;
    } else {
      env = foilStudioEnv(envDir) * F;
    }

    float a = dot(H, s.N) * 2.5 + s.uv0.y * 1.2 + s.fromCenter * 0.8;
    vec3 irid;
    if (uThinFilmOn > 0.5) {
      float cosTheta = clamp(dot(s.V, H), 0.0, 1.0);
      irid = foilThinFilm(cosTheta, uFilmThickness, uFilmIor);
    } else {
      irid = foilMetallicRamp(a);
    }
    vec3 tint = mix(vec3(1.0), irid, uIridTint);

    float cellSize = s.texel.x * uSparkleDensity;
    vec2 cellId = floor(s.puv / cellSize);
    vec2 cf = fract(s.puv / cellSize) - 0.5;
    float r1 = foilHash(cellId);
    float r2 = foilHash(cellId + 19.7);
    vec2 flakeDir = normalize(vec2(r1, r2) * 2.0 - 1.0);
    vec2 td = s.lightXY / (length(s.lightXY) + 1e-3);
    float align = dot(td, flakeDir);
    float pointShape = 1.0 - smoothstep(0.0, 0.16, length(cf));
    float flash = smoothstep(0.82, 1.0, align) * step(uSparkleThresh, r1) * pointShape;

    // Multiscale microfacet glint (Chermain 2020 / Zirr 2016 inspired, but a
    // procedural slope-matching approximation, NOT a precomputed dictionary).
    // A surface microfacet glints when its slope matches the half-vector slope.
    // We project H to slope space, then over a few octaves of cell grids draw a
    // random microfacet slope per cell and add a sharp lobe weighted by how
    // closely that slope matches hSlope. Because hSlope comes from H (which
    // already folds in the rest light + tilt), glints twinkle as light/view
    // change, not merely when tilt aligns to a fixed flake direction.
    vec2 hSlope = H.xy / max(H.z, 1e-3);
    float glint = 0.0;
    float baseCell = 1.0 / max(uGlintDensity, 1.0);
    const int GLINT_OCTAVES = 3;
    float octWeight = 0.0;
    for (int o = 0; o < GLINT_OCTAVES; o++) {
      float scale = pow(2.0, float(o));
      float cellSizeOct = baseCell / scale;
      vec2 gid = floor(s.puv / cellSizeOct) + vec2(float(o) * 37.0, float(o) * 53.0);
      // Random microfacet slope for this cell, spread ~ roughness.
      vec2 mSlope = (foilHash2(gid) * 2.0 - 1.0) * uGlintSpread;
      vec2 d = hSlope - mSlope;
      float lobe = exp(-uGlintSharp * dot(d, d));
      // Per-cell random gate keeps glints sparse / discrete (sparkle look).
      float gate = step(0.6, foilHash(gid + 11.3));
      // Finer octaves contribute a little less so coarse glints dominate.
      float w = 1.0 / scale;
      glint += lobe * gate * w;
      octWeight += w;
    }
    glint /= max(octWeight, 1e-3);

    // DOM gates the foil intensity by tilt magnitude (motion only on tilt). The
    // board has no per-card tilt. Drive it from the specular alignment instead
    // (how close the half-vector is to the surface normal) so the foil flares
    // as the highlight crosses the card during a camera orbit, then sits at the
    // rest floor otherwise.
    float master = (s.sceneLit > 0.5)
      ? clamp(ndh * 1.6, 0.0, 1.0)
      : clamp(length(s.tilt) * 2.2, 0.0, 1.0);
    float intensity = max(master, uRestFloor);

    vec3 foil = vec3(0.0);
    // Tint the metal reflection toward a vivid spatial rainbow (uMetalTint) so
    // the metal shimmers colour like the deck-builder holo instead of reading
    // as neutral silver. Uses a saturation-boosted cosine rainbow that varies
    // across the card + with the half-vector (so it shifts as you tilt),
    // independent of the realistic thin-film. 0 = silver, 1 = full rainbow.
    // Deck-builder holo, faithfully: the 5-colour ramp along a 110-degree
    // axis, ~half a cycle across the card, slid by the tilt POSITION (no
    // view-angle term. That is what kept the legacy smooth, not flickery).
    // At rest (s.tilt = 0) this is the static rainbow gradient the deck builder
    // shows; tilting slides it.
    float holoAxis = s.uv0.x * 0.94 + s.uv0.y * 0.34;
    // DOM slides the rainbow by tilt POSITION; board slides it by the view
    // direction so the hue shifts as the camera orbits the table.
    float holoSlide = (s.sceneLit > 0.5)
      ? (s.V.x * 0.94 + s.V.y * 0.34) * uHueShift
      : (s.tilt.x * 0.94 + s.tilt.y * 0.34) * uHueShift;
    float holoArg = holoAxis * 0.6 + holoSlide;
    // Depth-parallax the holographic layer: near pixels (high depth) slide the
    // rainbow further than far pixels, so the sheen reads as a refractive film
    // floating ABOVE the art and shifting with tilt. Depth lives in the s.shine,
    // not in moved pixels. Keyed to the same direction as holoSlide (tilt on
    // DOM, view on the board). No effect without a baked depth map.
    vec2 shineDir = (s.sceneLit > 0.5) ? s.V.xy : s.tilt;
    holoArg += s.depthDelta * (shineDir.x * 0.94 + shineDir.y * 0.34) * uDepthShine;
    // Default: the smooth, metallic cosine palette. uDeckRamp swaps in the
    // vivid 5-colour deck-builder ramp for an exact-match A/B.
    vec3 metalRainbow = (uDeckRamp > 0.5) ? foilRainbow5(holoArg) : foilSatBoost(foilMetallicRamp(holoArg), 1.7);
    foil += env * s.shine * uEnvStr * uEnvOn * s.glareGuard;
    foil += ggx * tint * (0.85 * s.relief + 0.55 * s.shine) * uGgxStr * uGgxOn * s.glareGuard;
    foil += irid * s.shine * uIridStr * uIridOn;
    // Two INDEPENDENT sparkle layers, each its own toggle: legacy hashed
    // (uSparkleOn) and the half-vector microfacet glint (uGlintOn).
    foil += vec3(1.0) * flash * s.shine * uSparkleStr * uSparkleOn;
    foil += vec3(1.0) * glint * s.shine * uGlintBrightness * uGlintOn;
    // Metallic layers above are restricted to the white substrate inside the
    // text zones so the lettering stays crisp.
    foil *= s.zoneFoil;
    // Emboss is STRUCTURAL (the raised box edge). Applied after the white-mask
    // gate so it is not killed by it, and not gated by s.shine.
    foil += ggx * s.bevel * uEmbossStr;
    foil *= intensity;

    float shadow = max(-dot(s.N, s.Lr), 0.0) * s.bevel * master * uShadowStr;

    // Always-on rest signature: a static spatial holo film on the light areas
    // (NOT tilt-gated), so the card reads as foil even when flat. Dark areas
    // (low s.shine) are untouched, so text stays clean.
    // Rest sheen uses the SAME deck-builder rainbow (static gradient at rest),
    // on the light areas, so the resting colours match the deck builder too.
    vec3 restSheen = metalRainbow * s.shine * uRestHolo;

    // Energy-bounded combine. The additive foil layers blow the whole card to
    // white at the specular peak (looks cheap), so: Reinhard-compress the foil
    // (asymptotes below 1) then SCREEN it over the s.base. Screen never exceeds
    // white AND adds little where the s.base is already bright, so the art stays
    // legible at every angle instead of clipping to a flat white wash.
    vec3 baseShadowed = s.base * (1.0 - shadow);
    vec3 f = (foil + restSheen);
    f = f / (1.0 + f);
    vec3 outc = baseShadowed + f * (1.0 - baseShadowed);
    // Hue-filter the light/metal areas toward the spatial rainbow (deck-builder
    // colour-dodge style): recolour the bright areas so the metal shows real
    // hues instead of staying silver. Dark art (low s.shine) is untouched.
    // metalTint = amount; the rainbow varies across the card + shifts on tilt.
    // Iridescent sheen on the LIGHT parts only (like real Sorcery foils):
    // gated by s.shine, so the lit fur / bright areas get a metallic colour
    // shimmer while the mid-tone subject keeps its own hue (fox stays orange)
    // and the dark background stays matte. It's a CHROMA add (rainbow minus
    // grey), not a recolour, so it preserves the art. Dark text kept out via
    // s.zoneFoil.
    vec3 holoChroma = (metalRainbow - 0.5) * foilLuma(outc);
    outc += holoChroma * uMetalTint * s.zoneFoil * s.shine * 1.8;

    // White (#FFFFFF) parts. Text-box strokes, the card name, white art
    // highlights. Can't gain hue from an additive sheen (already maxed), so
    // RECOLOUR them toward the rainbow: white reads as metallic colour foil.
    // Gated by s.whiteMask so coloured art (fox body) is untouched. Confined to
    // LIGHT-mode cards (s.foilMode < 0.33): on DARK/COLOUR cards the foil target
    // isn't the white substrate, so this white recolour must NOT also fire. That
    // double-foiling (mode s.shine + white accents here) is the "both applied" bug.
    float lightModeOnly = 1.0 - step(0.33, s.foilMode);
    outc = mix(outc, outc * metalRainbow * 1.8, clamp(s.whiteMask * uMetalTint * 1.5 * lightModeOnly, 0.0, 1.0));

    return outc;
  }
`;
